import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Blocks, CheckCircle2, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { usePluginDetail } from "@/api/queries/plugins";
import { useManagedRepos } from "@/api/queries/gitContent";
import { useArchiveRepo } from "@/api/mutations/gitContent";
import { useApproveDraft, useDiscardDraft, useSubmitDraft } from "@/api/mutations/repoLifecycle";
import { feldAenderungLabel, repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { DraftChangesDialog } from "@/components/repo-lifecycle/DraftChangesDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { RepoContentPanel } from "@/components/repo-content/RepoContentPanel";
import { CatalogSourceAlert, PluginStatusBadge } from "@/components/plugin-catalog/CatalogStatus";
import { PluginApprovedToggle, PluginOwnerToggle } from "@/components/plugin-catalog/PluginDecisionCells";
import { FreischaltenNachFreigabeDialog } from "@/components/plugin-catalog/FreischaltenNachFreigabeDialog";
import { OwnerValue } from "@/components/ownership/SetOwnerDialog";
import { UpdatePluginMetaDialog } from "@/components/plugin-catalog/UpdatePluginMetaDialog";
import { PluginApproveDialog } from "@/components/plugin-catalog/PluginApproveDialog";
import { UpdateAgentsNachFreigabeDialog } from "@/components/plugin-catalog/UpdateAgentsNachFreigabeDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { ApiError } from "@/api/client";

// #290 (UC-3): Plugin-Detail — EIN Ort für ein Plugin. Vorher war das Wissen dreigeteilt
// (Meta+Freigaben /plugins · Dateien /repositories · Zuweisung Agent-Detail); hier steht
// alles zusammen, und bei einem eigenen Repo (plg-, Server-Zusage `repoId`) ist der
// geteilte Content-Baustein direkt eingebettet.
export function PluginDetail() {
  const { id, pluginName } = useParams<{ id: string; pluginName: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = usePluginDetail(id, pluginName);
  const repos = useManagedRepos();
  const archive = useArchiveRepo();
  const approve = useApproveDraft();
  const discard = useDiscardDraft();
  const submit = useSubmitDraft();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [verwerfenOpen, setVerwerfenOpen] = useState(false);
  const [freischaltenOpen, setFreischaltenOpen] = useState(false);
  const [metaOpen, setMetaOpen] = useState(false); // #292 (UC-4)
  // #293 (UC-2): ausstehende Änderung — Verwerfen-Bestätigung + Diff-Dialog am Banner.
  const [changeVerwerfenOpen, setChangeVerwerfenOpen] = useState(false);
  const [changeAnsehenOpen, setChangeAnsehenOpen] = useState(false);
  // #294 (UC-0/UC-4): Freigeben mit optionaler Version + Ausrollen nach Änderungs-Freigabe.
  const [approveOpen, setApproveOpen] = useState<"aufnahme" | "aenderung" | null>(null);
  const [ausrollenOpen, setAusrollenOpen] = useState(false);
  // #291 (Arbens Live-Fund): in-marketplace-Plugins liegen IM Katalog-Repo — das Panel
  // startet dann direkt im Plugin-Ordner (einmalig; danach navigiert der Admin frei).
  // #292 (UC-1): bei in-Aufnahme existieren die Dateien nur auf dem ENTWURFS-Branch —
  // das Panel startet dann zusätzlich auf `ref=<draftBranch>` (dieselbe Einmaligkeit).
  const [searchParams, setSearchParams] = useSearchParams();
  const repoPath = data?.repoPath ?? null;
  const draftRef = data?.phase === "in-aufnahme" ? (data.draft?.branch ?? null) : null;
  useEffect(() => {
    if (repoPath && !searchParams.get("path") && !searchParams.get("file")) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("path", repoPath);
          if (draftRef && !prev.get("ref")) next.set("ref", draftRef);
          return next;
        },
        { replace: true },
      );
    }
    // bewusst nur beim Eintreffen der Zusage — nicht bei jeder Navigation erneut
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoPath, draftRef]);
  const repo = data?.repoId ? ((repos.data ?? []).find((r) => r.id === data.repoId) ?? null) : null;

  const errorMessage =
    error instanceof ApiError && error.status === 404
      ? `Plugin "${pluginName}" nicht gefunden`
      : (error as Error | undefined)?.message;

  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Plugin…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to={id ? `/plugins/marketplaces/${encodeURIComponent(id)}` : "/plugins"} label="Marketplace" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const p = data.plugin;
  // #292 (UC-1): dieselbe Detail-Seite für ALLE Zustände — vor der Katalog-Freigabe ist
  // das Plugin „in Aufnahme" (Meta aus dem Entwurfs-Manifest, Aktionen = Lifecycle).
  const inAufnahme = data.phase === "in-aufnahme" && !!data.draft;
  const draft = data.draft ?? null;

  // #294 (UC-0): Freigeben läuft über den Versions-Dialog — nach der Freigabe URL/Cache
  // aufräumen und die passende Verkettung anschließen.
  function nachFreigabe(art: "aufnahme" | "aenderung") {
    // Panel zeigt sonst den gemergten (toten) Branch → zurück auf live.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("ref");
        next.delete("file");
        next.delete("path");
        return next;
      },
      { replace: true },
    );
    if (art === "aufnahme") setFreischaltenOpen(true); // #288: gleich freischalten?
    else setAusrollenOpen(true); // #294 (UC-4): betroffene Agents jetzt aktualisieren?
    void refetch();
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo={`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}`}
        backLabel={p.marketplaceName}
        icon={Blocks}
        title={p.name}
        badge={
          inAufnahme ? (
            <Badge variant="busy" data-testid="plugin-phase-badge">
              {draft?.state === "submitted" ? "in Aufnahme — wartet auf Freigabe" : "Entwurf — noch nicht eingereicht"}
            </Badge>
          ) : p.status !== "available" ? (
            <PluginStatusBadge status={p.status} />
          ) : (
            <Badge variant="outline">Plugin</Badge>
          )
        }
        sub={p.description || "Keine Beschreibung im Manifest."}
        actions={
          <span className="flex items-center gap-2">
            {/* #292 (UC-4): Meta ändern = Entwurf — nur solange der Eintrag im Manifest
                steht (verwaist/unbekannt hat nichts zu patchen). */}
            {p.status === "available" && (
              <Button variant="outline" size="sm" onClick={() => setMetaOpen(true)} data-testid="plugin-meta-edit">
                <Pencil className="h-4 w-4" /> Meta bearbeiten
              </Button>
            )}
            {inAufnahme ? (
              <>
                {draft?.state === "submitted" && draft.canApprove && (
                  <Button size="sm" disabled={approve.isPending} onClick={() => setApproveOpen("aufnahme")} data-testid="plugin-draft-approve">
                    <CheckCircle2 className="h-4 w-4" /> Freigeben
                  </Button>
                )}
                {draft?.state === "draft" && (
                  <span className="text-[11.5px] text-text3">noch nicht eingereicht — unten im Datei-Panel einreichen</span>
                )}
                <Button variant="outline" size="sm" disabled={discard.isPending} onClick={() => setVerwerfenOpen(true)} data-testid="plugin-draft-discard">
                  <Trash2 className="h-4 w-4 text-danger" /> Verwerfen
                </Button>
              </>
            ) : (
              // Nur bei EIGENEM Repo (own-repo). Bei in-marketplace (repoPath gesetzt) wäre
              // das Ziel der ganze KATALOG — den löscht man über den Marketplace.
              data.repoId &&
              !data.repoPath && (
                <Button variant="destructive" size="sm" onClick={() => setArchiveOpen(true)} data-testid="plugin-repo-archive">
                  <Trash2 className="h-4 w-4" /> Repo löschen
                </Button>
              )
            )}
          </span>
        }
      />

      {/* Ladefehler der Quelle BENENNEN — nie als leerer Zustand rendern (#287). */}
      <CatalogSourceAlert sources={data.sources} />

      {/* #293 (UC-2): ausstehende Änderung — die Entscheidung hängt am FACHOBJEKT.
          Entfernung ist rot (die Freigabe nimmt das Plugin aus dem Katalog). */}
      {data.pendingChange && (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2.5 text-[12.5px] ${
            data.pendingChange.art === "entfernung" ? "border-danger/50 bg-danger/5" : "border-warn-border bg-warn-bg"
          }`}
          data-testid="plugin-pending-change"
        >
          <span className="font-semibold">
            {data.pendingChange.art === "entfernung"
              ? "Ausstehende Entfernung: dieses Plugin wird mit der Freigabe aus dem Katalog genommen."
              : // Review L7: leere Feld-Liste (Version-Skew/Randfall) → auf den Dialog verweisen
                (data.pendingChange.felder?.length ?? 0) > 0
                ? `Ausstehende Änderung: ${feldAenderungLabel(data.pendingChange.felder ?? [])}`
                : "Ausstehende Änderung — Details unter ‚Änderungen ansehen'."}
          </span>
          <Badge variant={data.pendingChange.state === "submitted" ? "busy" : "outline"}>
            {data.pendingChange.state === "submitted" ? "wartet auf Freigabe" : "noch nicht eingereicht"}
          </Badge>
          <span className="ml-auto flex items-center gap-1.5">
            <Button variant="outline" size="sm" onClick={() => setChangeAnsehenOpen(true)} data-testid="pending-change-view">
              Änderungen ansehen
            </Button>
            {/* Review M2: Einreichen ist eine Fach-Aktion — direkt hier, nicht in der Werkbank. */}
            {data.pendingChange.state === "draft" && (
              <Button
                variant="outline"
                size="sm"
                disabled={submit.isPending}
                onClick={() => {
                  const pc = data.pendingChange!;
                  submit.mutate(
                    { repoId: pc.repoId, branch: pc.branch, title: pc.art === "entfernung" ? `${p.name} entfernen` : `Änderung an ${p.name}` },
                    {
                      onSuccess: () => toast.success("Eingereicht — wartet jetzt auf Freigabe."),
                      onError: (err) => toast.error(repoErrorMessage(err, "Einreichen fehlgeschlagen")),
                    },
                  );
                }}
                data-testid="pending-change-submit"
              >
                Einreichen
              </Button>
            )}
            {data.pendingChange.state === "submitted" && data.pendingChange.canApprove && (
              <Button
                size="sm"
                variant={data.pendingChange.art === "entfernung" ? "destructive" : "default"}
                disabled={approve.isPending}
                onClick={() => {
                  const pc = data.pendingChange!;
                  // #294 (UC-0): Änderungen über den Versions-Dialog; Entfernung direkt
                  // (eine Version wäre dort sinnlos).
                  if (pc.art === "aenderung") {
                    setApproveOpen("aenderung");
                    return;
                  }
                  approve.mutate(
                    { repoId: pc.repoId, branch: pc.branch },
                    {
                      onSuccess: () => {
                        toast.success(`„${p.name}" ist aus dem Katalog entfernt.`);
                        navigate(`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}`);
                      },
                      onError: (err) => toast.error(repoErrorMessage(err, "Freigeben fehlgeschlagen")),
                    },
                  );
                }}
                data-testid="pending-change-approve"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Freigeben
              </Button>
            )}
            <Button variant="outline" size="sm" disabled={discard.isPending} onClick={() => setChangeVerwerfenOpen(true)} data-testid="pending-change-discard">
              <Trash2 className="h-3.5 w-3.5 text-danger" /> Verwerfen
            </Button>
          </span>
        </div>
      )}

      {data.pendingChange && changeAnsehenOpen && (
        <DraftChangesDialog
          open
          onOpenChange={setChangeAnsehenOpen}
          repoId={data.pendingChange.repoId}
          branch={data.pendingChange.branch}
          title={data.pendingChange.art === "entfernung" ? `Entfernung von ${p.name}` : `Änderung an ${p.name}`}
        />
      )}

      {data.pendingChange && (
        <ConfirmDialog
          open={changeVerwerfenOpen}
          onOpenChange={setChangeVerwerfenOpen}
          title="Ausstehende Änderung verwerfen"
          description={
            data.pendingChange.art === "entfernung"
              ? `Die ausstehende Entfernung von "${p.name}" wird verworfen — das Plugin bleibt im Katalog.`
              : `Die ausstehende Änderung an "${p.name}" wird verworfen — der Live-Stand bleibt unverändert. Das lässt sich nicht rückgängig machen.`
          }
          confirmText="Verwerfen"
          confirmVariant="destructive"
          isPending={discard.isPending}
          onConfirm={() => {
            const pc = data.pendingChange!;
            discard.mutate(
              { repoId: pc.repoId, branch: pc.branch },
              {
                onSuccess: () => {
                  toast.success("Änderung verworfen");
                  setChangeVerwerfenOpen(false);
                  void refetch();
                },
                onError: (err) => toast.error(repoErrorMessage(err, "Verwerfen fehlgeschlagen")),
              },
            );
          }}
        />
      )}

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="space-y-[18px]">
          {/* Vokabular (#291): das hier ist FREISCHALTEN (Plattform-Vertrauen) + ANBIETEN
              (Owner-Self-Service) — nicht die Entwurfs-Freigabe (die sitzt im Kopf/Panel). */}
          <SectionCard
            title="Freischaltung & Owner-Angebot"
            description="Dieselben Schalter wie im Katalog — hier mit Beschriftung."
          >
            {inAufnahme ? (
              // #292 (UC-1): sichtbar, aber gesperrt — die Schalter setzen eine Katalog-
              // Entscheidung voraus, die es vor der Freigabe des Entwurfs nicht gibt.
              <p className="text-[12.5px] text-text3" data-testid="plugin-decisions-locked">
                Erst nach der Katalog-Freigabe verfügbar — ein Plugin in Aufnahme ist noch nicht Teil des Katalogs
                und kann weder freigeschaltet noch Ownern angeboten werden.
              </p>
            ) : (
              /* Arbens Live-Fund: ohne die Tabellen-Spaltenköpfe war unklar, was die zwei
                 Checkboxen tun — hier bekommt jede ihr Label + ihre Bedeutung. */
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-1 text-[12.5px] font-semibold text-text">Plattform-Freischaltung</p>
                  <p className="mb-1.5 text-[11.5px] text-text3">
                    „Diesem Plugin vertrauen wir" — ohne sie ist es keinem Agent zuweisbar und wird nirgends ausgeliefert.
                  </p>
                  <PluginApprovedToggle p={p} />
                </div>
                <div>
                  <p className="mb-1 text-[12.5px] font-semibold text-text">Owner-Self-Service</p>
                  <p className="mb-1.5 text-[11.5px] text-text3">
                    Dürfen Agent-Owner (User) dieses Plugin selbst für ihre Agents aktivieren? Teilmenge des Freigeschalteten.
                  </p>
                  <PluginOwnerToggle p={p} />
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title={`Genutzt von (${data.usedBy.length})`}
            description="Agents mit einer Zuweisung dieses Plugins — auch abgeschaltete Zuweisungen zählen (sie leben bei Re-Aktivierung wieder auf)."
            noPad
          >
            {data.usedBy.length === 0 ? (
              <div className="p-[18px] text-[12.5px] text-text3">Keinem Agent zugewiesen.</div>
            ) : (
              <ul className="divide-y divide-border" data-testid="plugin-used-by">
                {data.usedBy.map((u) => (
                  <li key={u.agentId} className="flex items-center gap-3 px-[18px] py-2.5">
                    {u.agentName === null ? (
                      <Badge variant="orphaned">orphaned: {u.agentId}</Badge>
                    ) : (
                      <Link to={`/agents/${encodeURIComponent(u.agentId)}?tab=tools`} className="font-medium text-accent-text hover:underline">
                        {u.agentName}
                      </Link>
                    )}
                    <span className="mono text-[11px] text-text3">{u.agentId}</span>
                    <span className={`ml-auto text-[12px] ${u.enabled ? "text-ok-text" : "text-text3"}`}>
                      {u.enabled ? "aktiv" : "abgeschaltet"}
                    </span>
                    <Badge variant="outline">{u.scope}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* #292 (UC-1, bewusste Grenze): own-repo in Aufnahme — das eigene Repo ist bis
              zum Merge nicht in der Whitelist, ehrlich benennen statt leeres Panel. */}
          {inAufnahme && !data.repoId && (
            <SectionCard title="Dateien" testId="plugin-own-repo-pending">
              <p className="text-[12.5px] text-text3">
                Das Plugin bekommt ein eigenes Repository — es wird hier verwaltbar, sobald der
                Aufnahme-Entwurf freigegeben ist. Bis dahin: Entwurf am{" "}
                <Link to={`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}`} className="text-accent-text hover:underline">
                  Marketplace
                </Link>{" "}
                ansehen.
              </p>
            </SectionCard>
          )}

          {/* Eigenes Repo (Server-Zusage): der geteilte Content-Baustein — Dateien +
              Entwurf/Freigabe/Version, dieselbe Komponente wie /repositories. */}
          {data.repoId && (
            <div className="flex h-[calc(100vh-320px)] min-h-[440px] flex-col gap-3">
              {repos.isLoading ? (
                <SectionCard>
                  <div className="flex items-center gap-3 py-6 text-sm text-text3">
                    <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Repo-Inhalt…
                  </div>
                </SectionCard>
              ) : repo ? (
                <RepoContentPanel key={repo.id} repo={repo} />
              ) : (
                <SectionCard>
                  <p className="text-[13px] text-text3">Repo-Inhalt momentan nicht verfügbar.</p>
                </SectionCard>
              )}
            </div>
          )}
        </div>

        <PropGrid
          rows={[
            ["Marketplace", <Link to={`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}`} className="text-accent-text hover:underline">{p.marketplaceName}</Link>],
            [
              "Verantwortlich",
              // #292 (UC-2): GEERBT vom Marketplace — read-only hier; geändert wird
              // am Marketplace-Detail (eigene Plugin-Kante erst mit #280).
              <span className="flex items-center gap-1.5" data-testid="plugin-owner" title="Geerbt vom Marketplace — dort änderbar.">
                <OwnerValue ownerId={data.ownerId} />
                <span className="text-[11px] text-text3">(geerbt)</span>
              </span>,
            ],
            // #292 (UC-5): das ist die Version aus dem MANIFEST (Autor-Angabe) — die
            // Katalog-/Repo-Version (Tag) steht in der Lifecycle-Leiste des Datei-Panels.
            ["Version (Manifest)", p.version ?? <span className="text-text3">—</span>],
            ["Status", <PluginStatusBadge status={p.status} />],
            [
              "Dateien",
              data.repoId ? (
                data.repoPath ? (
                  <span className="mono text-xs" title="Das Plugin liegt IM Katalog-Repo — die Dateien unten zeigen diesen Ordner.">{data.repoPath}/ (im Katalog-Repo)</span>
                ) : (
                  <span className="mono text-xs">{data.repoId} (eigenes Repo)</span>
                )
              ) : inAufnahme ? (
                <span className="text-text3" title="Das eigene Repo wird nach der Freigabe des Aufnahme-Entwurfs hier verwaltbar.">eigenes Repo — nach Freigabe verwaltbar</span>
              ) : (
                <span className="text-text3" title="Der Katalog verweist auf keine bei uns managebare Quelle.">extern — nicht bei uns</span>
              ),
            ],
            ["Zuweisungen", String(data.usedBy.length)],
          ]}
        />
      </div>

      {/* #292 (UC-1): Verwerfen trifft den ganzen ENTWURF — hier ist es der Aufnahme-
          Entwurf dieses Plugins; hängen weitere Plugins daran, listet sie der Marketplace. */}
      <ConfirmDialog
        open={verwerfenOpen}
        onOpenChange={setVerwerfenOpen}
        title="Aufnahme-Entwurf verwerfen"
        description={`Der Aufnahme-Entwurf für "${p.name}" wird verworfen — das Plugin erscheint nicht im Katalog. Nimmt der Entwurf weitere Plugins auf, werden sie MIT verworfen. Ein bereits angelegtes eigenes Plugin-Repository bleibt bestehen (danach unter Repositories → „Nicht zugeordnet"). Das lässt sich nicht rückgängig machen.`}
        confirmText="Verwerfen"
        confirmVariant="destructive"
        isPending={discard.isPending}
        onConfirm={() => {
          if (!draft) return;
          discard.mutate(
            { repoId: draft.repoId, branch: draft.branch },
            {
              onSuccess: () => {
                toast.success("Entwurf verworfen");
                setVerwerfenOpen(false);
                // das Plugin existiert danach nicht mehr — zurück zum Marketplace
                navigate(`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}`);
              },
              onError: (err) => toast.error(repoErrorMessage(err, "Verwerfen fehlgeschlagen")),
            },
          );
        }}
      />

      {freischaltenOpen && (
        <FreischaltenNachFreigabeDialog marketplaceId={p.marketplaceId} open onOpenChange={setFreischaltenOpen} />
      )}

      {metaOpen && (
        <UpdatePluginMetaDialog open onOpenChange={setMetaOpen} plugin={p} inAufnahme={inAufnahme} />
      )}

      {/* #294 (UC-0): Freigeben mit optionaler Plugin-Version — Koordinaten je nach
          Zustand (Aufnahme-Entwurf vs. Änderungs-Entwurf). */}
      {approveOpen === "aufnahme" && draft && (
        <PluginApproveDialog
          open
          onOpenChange={(o) => !o && setApproveOpen(null)}
          marketplaceId={p.marketplaceId}
          pluginName={p.name}
          repoId={draft.repoId}
          branch={draft.branch}
          art="aufnahme"
          onApproved={() => {
            setApproveOpen(null);
            nachFreigabe("aufnahme");
          }}
        />
      )}
      {approveOpen === "aenderung" && data.pendingChange && (
        <PluginApproveDialog
          open
          onOpenChange={(o) => !o && setApproveOpen(null)}
          marketplaceId={p.marketplaceId}
          pluginName={p.name}
          repoId={data.pendingChange.repoId}
          branch={data.pendingChange.branch}
          art="aenderung"
          onApproved={() => {
            setApproveOpen(null);
            nachFreigabe("aenderung");
          }}
        />
      )}

      {ausrollenOpen && (
        <UpdateAgentsNachFreigabeDialog
          open
          onOpenChange={setAusrollenOpen}
          marketplaceId={p.marketplaceId}
          pluginName={p.name}
        />
      )}

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Plugin-Repo löschen"
        description="Das Repository wird archiviert (Inhalt und Historie bleiben unter einem datierten Namen). Der Katalog-Eintrag bleibt bestehen und zeigt danach ins Leere — das Plugin gilt als verwaist und wird nicht mehr ausgeliefert."
        confirmText="Löschen (archivieren)"
        confirmVariant="destructive"
        requiredTypeText={p.name}
        isPending={archive.isPending}
        onConfirm={() => {
          if (!data.repoId) return;
          archive.mutate(data.repoId, {
            onSuccess: ({ archivedAs }) => {
              toast.success(`Archiviert als ${archivedAs}`);
              setArchiveOpen(false);
              void refetch();
            },
            onError: (e) => toast.error(`Löschen fehlgeschlagen: ${(e as Error).message}`),
          });
        }}
      />
    </div>
  );
}
